import type {
  PriceListCategory,
  PriceListPricingMode,
  PriceListUnit,
} from "@xop/dashboard-shared";

import { api } from "../../../../lib/api.js";

/**
 * Native product-catalogue client. Talks to the dashboard server's CRM proxy
 * (`/api/crm/products*`), which mints a tenant-bound CRM token from the
 * dashboard session — so this works WITHOUT the CRM Redux store / CrmRoot
 * bootstrap. crm-api stays the system of record.
 *
 * TWO LEVELS since 2026-09-13: a product and the features inside it. There is
 * no version below a feature, and there is no price list beside it — a FEATURE
 * IS A PRICE-BOOK ROW, so the same call that renames one sets what it costs.
 */

export interface ProductFeature {
  id: number;
  name: string;
  code?: string | null;
  /** The PRINTED part number (700-2341-001). Falls back to the composed
   * `<product code>-<feature code>` when nobody has entered one, so the cell
   * is never blank. `code` is the 4-digit internal code the licence file uses
   * — the two are different identities and both travel. */
  part_number?: string | null;
  category: PriceListCategory;
  /** The APPLIANCE feature this row is linked to (2026-09-15) — the key a
   * licence file names when a customer buys this. Null until someone links it
   * here, and an unlinked Value Added Service / Platform Extension cannot go on
   * a licence, which is why the list says so per row. Unique per tenant. */
  licence_key?: string | null;
  unit: PriceListUnit;
  pricing_mode: PriceListPricingMode;
  /** Integer cents. Always 0 unless `pricing_mode === "fixed"`. */
  price_cents: number;
  percent_rate: number | null;
  currency: string;
  notes: string;
  /** Retired features stay readable for documents that name them, and are
   * offered in no picker. */
  active: boolean;
  sort_order: number;
  /** The server's verdict on "has anybody priced this?" — `fixed` at 0 is
   * unpriced, `included` is a decision. Don't re-derive it per render. */
  priced?: boolean;
  created_at?: string;
  updated_at?: string;
  updated_by_email?: string | null;
}

export interface Product {
  id: number;
  name: string;
  code?: string | null;
  features_count?: number;
  created_at?: string;
  features?: ProductFeature[];
}

/** Everything a feature write may set. Prices are CENTS, never dollars. */
export interface FeatureWrite {
  name: string;
  code?: string;
  part_number?: string;
  /** The appliance key to link, or null to unlink. */
  licence_key?: string | null;
  category?: PriceListCategory;
  unit?: PriceListUnit;
  pricing_mode?: PriceListPricingMode;
  price_cents?: number;
  percent_rate?: number | null;
  notes?: string;
  active?: boolean;
  sort_order?: number;
}

const BASE = "/api/crm";

function unwrap<T>(res: unknown, key: string): T {
  const r = res as Record<string, unknown>;
  if (r && typeof r === "object" && key in r) return r[key] as T;
  if (r && typeof r === "object" && "data" in r) return r.data as T;
  return res as T;
}

export async function listProducts(): Promise<Product[]> {
  const res = await api<unknown>(`${BASE}/products`);
  return unwrap<Product[]>(res, "products") ?? [];
}

export async function getProduct(id: number): Promise<Product> {
  const res = await api<unknown>(`${BASE}/products/${id}`);
  return unwrap<Product>(res, "product");
}

export const createProduct = (body: { name: string; code?: string }) =>
  api(`${BASE}/products`, { method: "POST", body: { product: body } });
export const updateProduct = (id: number, body: { name: string; code?: string }) =>
  api(`${BASE}/products/${id}`, { method: "PATCH", body: { product: body } });
export const deleteProduct = (id: number) =>
  api(`${BASE}/products/${id}`, { method: "DELETE" });

export const createFeature = (productId: number, body: FeatureWrite) =>
  api(`${BASE}/products/${productId}/product_features`, {
    method: "POST",
    body: { product_feature: body },
  });
export const updateFeature = (id: number, body: FeatureWrite) =>
  api(`${BASE}/product_features/${id}`, {
    method: "PATCH",
    body: { product_feature: body },
  });
export const deleteFeature = (id: number) =>
  api(`${BASE}/product_features/${id}`, { method: "DELETE" });
